import { Button, IconButton, Stack, Td } from '@chakra-ui/react'
import { DeleteIcon } from '@chakra-ui/icons'
import { Dispatch, SetStateAction } from 'react'
import {
  FrequencyUnit,
  AmountUnit,
  DurationUnit,
  Method,
  Percentage,
  Regimen,
  SelectedFields,
  Usage,
} from '../../types/Regimen'

interface ActionButtonsProps {
  usageIndex: number
  record: Regimen
  usage: Usage
  allowUpdate: boolean
  canReactivate?: boolean
  reportingForStart: string
  setEditingId: Dispatch<SetStateAction<string | undefined>>
  setEditingUsageIndex: Dispatch<SetStateAction<number | undefined>>
  setStartDate: Dispatch<SetStateAction<string>>
  setFrequency: Dispatch<SetStateAction<string>>
  setFrequencyUnit: Dispatch<SetStateAction<FrequencyUnit | undefined>>
  setAmount: Dispatch<SetStateAction<string>>
  setAmountUnit: Dispatch<SetStateAction<AmountUnit | undefined>>
  setMethod: Dispatch<SetStateAction<Method | undefined>>
  setSelectedFields: Dispatch<SetStateAction<SelectedFields>>
  setDuration: Dispatch<SetStateAction<string>>
  setDurationUnit: Dispatch<SetStateAction<DurationUnit | undefined>>
  setPercentage: Dispatch<SetStateAction<Percentage | undefined>>
  modal: { onOpen: () => void }
  setConfirmEndId: Dispatch<SetStateAction<string | undefined>>
  setConfirmDelete: Dispatch<
    SetStateAction<{ recordId: string; usageIndex: number } | undefined>
  >
  onReactivate: (recordId: string) => void
}

export default function ActionButtons({
  usageIndex,
  record,
  usage,
  allowUpdate,
  canReactivate = false,
  reportingForStart,
  setEditingId,
  setEditingUsageIndex,
  setStartDate,
  setFrequency,
  setFrequencyUnit,
  setAmount,
  setAmountUnit,
  setMethod,
  setSelectedFields,
  setDuration,
  setDurationUnit,
  setPercentage,
  modal,
  setConfirmEndId,
  setConfirmDelete,
  onReactivate,
}: ActionButtonsProps) {
  const handleStop = () => {
    setConfirmEndId(record.id)
  }

  const handleReactivate = () => {
    onReactivate(record.id)
  }

  const handleEdit = () => {
    // Use the specific usage for this row, not always the current one
    const editingUsage = usage
    setEditingId(record.id)
    setEditingUsageIndex(usageIndex)
    setStartDate(editingUsage.startDate)
    setFrequency(editingUsage.frequency?.toString() || '')
    setFrequencyUnit(editingUsage.frequencyUnit)
    setAmount(editingUsage.amount?.toString() || '')
    setAmountUnit(editingUsage.amountUnit)
    setMethod(editingUsage.method)
    setSelectedFields(editingUsage.fields as SelectedFields)
    setDuration(editingUsage.duration?.toString() || '')
    setDurationUnit(editingUsage.durationUnit)
    setPercentage(editingUsage.percentage)

    modal.onOpen()
  }

  const handleDelete = () => {
    setConfirmDelete({
      recordId: record.id,
      usageIndex,
    })
  }

  return (
    <Td>
      <Stack direction='row' spacing={1}>
        {usageIndex === 0 && !record.endDate && (
          <>
            {allowUpdate && (
              <Button
                size='sm'
                onClick={handleEdit}
                aria-label='Update regimen'
              >
                Update
              </Button>
            )}
            <Button
              size='sm'
              colorScheme='red'
              onClick={handleStop}
              aria-label='Stop regimen'
            >
              Stop
            </Button>
          </>
        )}
        {/* Show Reactivate button for ended regimens (current usage only) */}
        {usageIndex === 0 && record.endDate && canReactivate && (
          <Button
            size='sm'
            colorScheme='green'
            onClick={handleReactivate}
            aria-label='Reactivate regimen'
          >
            Reactivate
          </Button>
        )}
        {usage.startDate >= reportingForStart && (
          <IconButton
            aria-label='Delete regimen entry'
            ml={1}
            size='sm'
            colorScheme='red'
            variant='outline'
            onClick={handleDelete}
            icon={<DeleteIcon />}
          />
        )}
      </Stack>
    </Td>
  )
}
